import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { EventType, MatrixClient, MsgType, NotificationCountType, Room, RoomEvent } from 'matrix-js-sdk';
import { MatrixEvent } from 'matrix-js-sdk/lib/models/event';
import { useEffect, useState } from 'react';
import { organizationRolesAsString } from 'utils/organizationRolesAsString';
import { BpMatrixClient, ConversationType, RoomNames } from '../../utils/matrixClient';
import { getFullyQualifiedUser, getUuidFromFullyQualifiedUser } from '../../utils/matrixRoomId';
import { useAuthClaims } from '../useAuthClaims';
import { MatrixTokenType } from 'utils/backendApi';
import { useQuery } from 'urql';
import { useGetMatrixToken } from './useGetMatrixToken';
import { BpRoomType } from './notifications/useNotifications';

export type RoomInformation = {
  displayName: string;
  type: RoomNames;
  profile?: { name: string; uuid: string; roleName: string };
};
export type ExtendedRoom = Room & { roomInformation?: RoomInformation | null };

const UseMatrixClientDocument = graphql(`
  query UseMatrixClient($organizationUuid: ID!) {
    ...GetConversationsFieldsDocument
    ...ResolveRoomNameFields
  }
`);

export const useMatrixClient = () => {
  const [client, setClient] = useState<MatrixClient | null>(null);
  const [rooms, setRooms] = useState<Room[] | null>(null);
  const [hasUnread, setHasUnread] = useState<boolean>(false);

  const isAvailable = Boolean(client?.isLoggedIn()) && Boolean(client?.isInitialSyncComplete());

  const token = useGetMatrixToken();

  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: UseMatrixClientDocument,
    variables: {
      organizationUuid,
    },
  });

  const markAsRead = async (room?: Room | null) => {
    if (room) {
      const timeline = room.getLiveTimeline();
      const events = timeline.getEvents();
      if (events.length) {
        const lastEvent = events[events.length - 1];
        if (lastEvent) {
          await client?.setRoomReadMarkers(room.roomId, lastEvent.getId() ?? '');
          await client?.sendReadReceipt(lastEvent);
          room?.setTimelineNeedsRefresh(true);
        }
      }
    }
  };

  const markAllAsUnread = async () => {
    setHasUnread(false);

    const rooms = client?.getVisibleRooms().filter((room) => {
      const parts = room?.name.split(':')[0]?.split('_');
      const roomType: BpRoomType = parts[0] as BpRoomType;
      return roomType !== BpRoomType.DM;
    });

    rooms?.forEach((room) => {
      markAsRead(room);
    });
  };

  const getConversationsFromRoom = (room: Room | null | undefined) =>
    getConversations({
      room,
      profileUuid,
      query: data,
      token,
      userId: client?.getUserId(),
    });

  const getMatrixDmRooms = (searchValue?: string) => {
    const extendedRooms: ExtendedRoom[] | undefined = rooms?.map((room: ExtendedRoom) => {
      const name = room.name;
      room.roomInformation = resolveRoomName(name, data, profileUuid);
      return room;
    });

    const dmRooms = extendedRooms?.filter((room) => {
      return room.name?.startsWith('dm_');
    });

    if (searchValue && searchValue !== '')
      return dmRooms?.filter((room) => {
        if (room.roomInformation)
          return room.roomInformation.displayName.toLowerCase().includes(searchValue.toLowerCase());
        return true;
      });

    return dmRooms;
  };

  useEffect(() => {
    let active = true; // To prevent setting state on unmounted component

    BpMatrixClient.instance().then((fetchedClient) => {
      if (active && fetchedClient) {
        setClient(fetchedClient);
        const rooms = fetchedClient
          ?.getVisibleRooms()
          ?.sort((a, b) => b.getLastActiveTimestamp() - a.getLastActiveTimestamp());
        setRooms(rooms ?? []);

        const onEvent = async () => {
          const currentClient = await BpMatrixClient.instance();
          if (!currentClient) return {};

          const rooms = currentClient
            .getVisibleRooms()
            ?.sort((a, b) => b.getLastActiveTimestamp() - a.getLastActiveTimestamp());
          setRooms(rooms);

          const hasUnread =
            rooms?.some((room) => {
              const parts = room?.name.split(':')[0]?.split('_');
              const roomType: BpRoomType = parts[0] as BpRoomType;
              return roomType !== BpRoomType.DM && room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0;
            }) ?? false;
          setHasUnread(hasUnread);
        };

        fetchedClient.on(RoomEvent.Timeline, onEvent);
        fetchedClient.on(RoomEvent.Redaction, onEvent);

        // Cleanup function to remove listeners
        return () => {
          active = false;
          fetchedClient.removeListener(RoomEvent.Timeline, onEvent);
          fetchedClient.removeListener(RoomEvent.Redaction, onEvent);
        };
      }
    });

    return () => {
      active = false; // Prevent setting state if component unmounts before promise resolves
    };
  }, []);

  return {
    client,
    isAvailable,
    rooms,
    getMatrixRooms: getMatrixDmRooms,
    resolveRoomName: (name: string) => resolveRoomName(name, data, profileUuid),
    getConversationsFromRoom,
    markAllAsUnread,
    markAsRead,
    hasUnread,
  };
};

const ResolveRoomNameFieldsDocument = graphql(`
  fragment ResolveRoomNameFields on Query {
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      firstName
      lastName
      organizationRoles
    }
  }
`);

const resolveRoomName = (
  name: string,
  query: FragmentType<typeof ResolveRoomNameFieldsDocument> | undefined,
  profileUuid: string,
): RoomInformation | null => {
  const fragment = getFragmentData(ResolveRoomNameFieldsDocument, query);
  const profiles = fragment?.profiles;

  const parts = name.split(':')[0]?.split('_');
  if (parts[0] === RoomNames.Direct && profiles && profiles.length > 0) {
    const profile1 = profiles.find((profile) => {
      return profile.uuid === parts[1];
    });
    const profile2 = profiles.find((profile) => {
      return profile.uuid === parts[2];
    });
    const profileToShow = profileUuid === profile1?.uuid ? profile2 : profile1;
    if (profileToShow) {
      return {
        displayName: `${profileToShow.firstName} ${profileToShow.lastName}`,
        type: RoomNames.Direct,
        profile: {
          uuid: profileToShow.uuid,
          name: profileToShow.firstName ?? '',
          roleName: organizationRolesAsString(profileToShow.organizationRoles),
        },
      };
    }
  }
  return {
    displayName: name,
    type: RoomNames.Unknown,
  };
};

const GetConversationsFieldsDocument = graphql(`
  fragment GetConversationsFieldsDocument on Query {
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      displayNameShort
    }
  }
`);

export const getConversations = ({
  room,
  profileUuid,
  token,
  query,
  userId,
}: {
  room: Room | undefined | null;
  profileUuid: string;
  token: MatrixTokenType | undefined;
  query: FragmentType<typeof GetConversationsFieldsDocument> | undefined;
  userId: string | null | undefined;
}): ConversationType => {
  const events = room?.getLiveTimeline().getEvents();
  const fragment = getFragmentData(GetConversationsFieldsDocument, query);
  const profiles = fragment?.profiles;

  const matrixUser = token ? getFullyQualifiedUser(profileUuid, token?.home_server) : '';
  const conversation: ConversationType = [];

  let last: MatrixEvent | null = null;
  events?.forEach((matrixEvent, index) => {
    if (matrixEvent.getType() === EventType.RoomMessage) {
      if (matrixEvent.getRoomId() === room?.roomId && matrixEvent.getContent().msgtype === MsgType.Text) {
        const profile = profiles?.find((profile) => {
          if (token && matrixEvent.sender) {
            return profile.uuid === getUuidFromFullyQualifiedUser(matrixEvent.sender.userId ?? '', token.home_server);
          }
          return null;
        });
        if (
          events &&
          events[index - 1] &&
          conversation[conversation.length - 1] &&
          conversation[conversation.length - 1].messages &&
          matrixEvent.sender?.userId === last?.sender?.userId
        ) {
          conversation[conversation.length - 1].messages.push({
            type: '',
            content: matrixEvent.getContent().body,
            date: matrixEvent.getDate() ?? new Date(),
            matrixEvent: matrixEvent,
          });
        } else {
          conversation.push({
            messages: [
              {
                type: '',
                content: matrixEvent.getContent().body,
                date: matrixEvent.getDate() ?? new Date(),
                matrixEvent: matrixEvent,
              },
            ],
            isIncoming: matrixEvent.sender?.userId !== matrixUser,
            color: matrixEvent.getSender() === userId ? 'red' : 'blue',
            initials: profile && profile.displayNameShort ? `${profile.displayNameShort}` : '',
            id: matrixEvent.getId() ?? '',
          });
        }
        last = matrixEvent;
      }
    }
  });

  return conversation;
};
